/**
 * Lightweight, fire-and-forget telemetry for auth/session/logout/bfcache events.
 *
 * - Never throws (never breaks UX).
 * - Coalesces duplicate events within 2s to avoid floods.
 * - Truncates payloads to satisfy DB CHECK constraints on `client_events`.
 * - Safe to call from anywhere (anon or authed). Profile id is auto-attached
 *   from localStorage when available.
 */

import { supabase } from "@/integrations/supabase/client";

type Severity = "info" | "warn" | "error";

interface LogPayload {
  detail?: Record<string, unknown>;
  severity?: Severity;
}

const recent = new Map<string, number>();
const DEDUPE_WINDOW_MS = 2000;

function clamp(s: string | null | undefined, max: number): string | null {
  if (!s) return null;
  return s.length > max ? s.slice(0, max) : s;
}

function safeProfileId(): string | null {
  try {
    return localStorage.getItem("sirppi_profile_id");
  } catch {
    return null;
  }
}

export function logEvent(eventName: string, payload: LogPayload = {}): void {
  try {
    if (typeof window === "undefined") return;

    const key = `${eventName}:${JSON.stringify(payload.detail ?? {})}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recent.set(key, now);

    // Best-effort detail size guard (DB enforces 4000 char json max).
    let detail: Record<string, unknown> = payload.detail ?? {};
    let serialized = JSON.stringify(detail);
    if (serialized.length > 3500) {
      detail = { _truncated: true, preview: serialized.slice(0, 3000) };
      serialized = JSON.stringify(detail);
    }

    const row = {
      event_name: clamp(eventName, 80) ?? "unknown",
      severity: payload.severity ?? "info",
      route: clamp(window.location?.pathname, 300),
      user_agent: clamp(window.navigator?.userAgent, 500),
      user_profile_id: safeProfileId(),
      detail,
    };

    // Fire and forget — do not await, do not surface errors.
    // Cast through `any` because generated DB types lag a moment behind a
    // brand-new table; runtime insert is fine.
    void (supabase.from("client_events" as any).insert(row as any) as any).then(
      ({ error }: { error: { message: string } | null }) => {
        if (error && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[telemetry] insert failed:", error.message);
        }
      },
    );
  } catch {
    /* swallow — telemetry must never break the app */
  }
}
